import { useEffect, useState, useSyncExternalStore } from 'react';
import api from '../../../services/api';
export const markets = ['Houston', 'DFW', 'Memphis', 'Savannah', 'Charleston', 'Baltimore', 'Norfolk', 'Mobile'];
export const origins: Record<string, string[]> = {
  Houston: ['Bayport', 'Barbours Cut', 'Jacintoport', 'Houston rail ramp'],
  DFW: ['UP DIT', 'BNSF', 'KCS'],
  Memphis: ['BN', 'CSX', 'UP', 'NS', 'CN'],
  Savannah: ['Garden City'],
  Charleston: ['Wando Welch'],
  Baltimore: ['Seagirt'],
  Norfolk: ['NIT', 'PPCY'],
  Mobile: ['APM Terminal']
};
export const reasons = ['New customer request', 'Customer escalation', 'New / added volume', 'LFD / per diem risk', 'Storage or hold cleared', 'Driver rejected / failed', 'Reschedule after service failure', 'Appointment changed by receiver', 'Receiver closed', 'Split / partial load', 'Wrong yard or location', 'Equipment recovery', 'New lane / trial volume', 'Other'];
export const preOrderReasons = ['New lane / trial volume', 'New / added volume', 'New customer request', 'Customer escalation', 'Other'];
export type Status = 'PENDING' | 'COUNTERED' | 'NEEDS_INFO' | 'APPROVED' | 'DENIED' | 'CANCELLED' | 'COMPLETED';
export interface OrderContext {
  handling?: '' | 'Live unload' | 'Drop';
  dispatcherRegion?: string;
  dispatcher?: string;
  pro: string;
  lc?: string;
  customer: string;
  market: string;
  move?: string;
  container?: string;
  lfd?: string;
  erd?: string;
  cutoff?: string;
  miles?: string;
  keyTier?: string;
  appointment?: string;
  availableAt?: number;
  pool?: string;
  size?: string;
  pickup?: string;
  delivery?: string;
}
export interface Load {
  date: string;
  touched: boolean;
  quantity: number;
  size: string;
  origin: string;
  destination: string;
  miles: string;
  lfd: string;
  erd: string;
  cutoff: string;
}
export interface CapacityRequest {
  id: number;
  version?: number;
  source: 'PRO' | 'PRE-ORDER';
  proRequests?: Pick<CapacityRequest, 'pro' | 'order' | 'date' | 'miles' | 'customer' | 'market' | 'move' | 'pool' | 'lfd' | 'erd' | 'cutoff' | 'keyTier' | 'escalation' | 'lc' | 'handling'>[];
  order?: OrderContext;
  pro?: string;
  linkedPros: string[];
  batch?: string;
  parentId?: number;
  customer: string;
  market: string;
  move: string;
  pool: string;
  type: string;
  leg: string;
  urgency: string;
  for: string;
  units: number;
  exam: boolean;
  examLfd: string;
  examSite: string;
  examOffBy: string;
  date: string;
  asap: boolean;
  lfd: string;
  erd: string;
  cutoff: string;
  miles: string;
  keyTier: string;
  quote: string;
  quoteRate: string;
  handling?: '' | 'Live unload' | 'Drop';
  appointment: string;
  windowStart: string;
  windowEnd: string;
  reason: string;
  ask: string;
  commitments: string;
  requester: string;
  requesterName?: string;
  requesterTeam?: 'LC' | 'Sales' | 'Dispatch' | 'Other';
  requesterLcGroups?: string[];
  lc?: string;
  raisedAt: number;
  dueAt?: number;
  status: Status;
  offeredDate?: string;
  approvedDate?: string;
  loads: Load[];
  margin: string;
  cadence: string;
  escalation?: { manager: string; justification: string; reasons: string[] };
  files?: { id: number; name: string }[];
  attachments: string[];
  thread: string[];
  events?: { id: number; actor: string; createdAt: number; message: string }[];
  lastResponse?: { action: 'APPROVED' | 'DENIED' | 'CANCELLED' | 'COMPLETED' | 'COUNTERED' | 'NEEDS_INFO' | 'ACCEPT' | 'DECLINE'; actor: string; createdAt: number } | null;
}
export const requestProNumbers = (row: CapacityRequest): string[] => row.proRequests?.length ? row.proRequests.flatMap(member => member.pro ? [member.pro] : []) : row.pro ? [row.pro] : [];
export const isRequestForPro = (row: CapacityRequest, pro: string) => requestProNumbers(row).includes(pro) || row.linkedPros.includes(pro);
export const isRequestedBy = (row: CapacityRequest, email: string | undefined): boolean =>
  !!email?.trim() && row.requester.trim().toLowerCase() === email.trim().toLowerCase();
export const isOpen = (r: CapacityRequest) => ['PENDING', 'COUNTERED', 'NEEDS_INFO'].includes(r.status);
export function sortRequests(rows: CapacityRequest[]) {
  const ranks: Record<string, number> = {
    Critical: 0,
    Hot: 1,
    Routine: 2
  };
  return [...rows].sort((a, b) => Number(!isOpen(a)) - Number(!isOpen(b)) || (a.dueAt ?? Infinity) - (b.dueAt ?? Infinity) || ranks[a.urgency] - ranks[b.urgency] || a.id - b.id);
}
export function applySharedDate<T extends {
  date: string;
  touched: boolean;
}>(rows: T[], date: string): T[] {
  return rows.map(r => r.touched ? r : {
    ...r,
    date
  });
}
export function decide(r: CapacityRequest, status: Status, date: string, comment: string): CapacityRequest {
  if (!['APPROVED', 'CANCELLED'].includes(status) && !comment.trim()) throw new Error('Enter a comment for the requester.');
  if (status === 'COUNTERED' && !date) throw new Error('Choose an offered date.');
  return {
    ...r,
    status,
    offeredDate: status === 'COUNTERED' ? date : r.offeredDate,
    thread: [...r.thread, `${status.replace('_', ' ')}${date ? ` · ${date}` : ''}${comment ? ` — ${comment}` : ''}`]
  };
}
export const blankLoad = (): Load => ({
  date: '',
  touched: false,
  quantity: 1,
  size: '40HC',
  origin: '',
  destination: '',
  miles: '',
  lfd: '',
  erd: '',
  cutoff: ''
});
export function blankRequest(): CapacityRequest {
  return {
    id: 0,
    source: 'PRE-ORDER',
    linkedPros: [],
    customer: '',
    market: 'Houston',
    move: 'IMPORT',
    pool: 'Local',
    type: 'Capacity',
    leg: 'Delivery',
    urgency: 'Routine',
    for: 'Container move',
    units: 1,
    exam: false,
    examLfd: '',
    examSite: '',
    examOffBy: '',
    date: '',
    asap: false,
    lfd: '',
    erd: '',
    cutoff: '',
    miles: '',
    keyTier: '',
    quote: '',
    quoteRate: '',
    handling: '',
    appointment: 'Open receiving',
    windowStart: '',
    windowEnd: '',
    reason: 'New customer request',
    ask: '',
    commitments: '',
    requester: '',
    raisedAt: Date.now(),
    status: 'PENDING',
    loads: [blankLoad()],
    margin: '',
    cadence: '',
    attachments: [],
    thread: []
  };
}
export interface MarketNote {
  text: string;
  author: string;
  updatedAt: number;
  version?: number;
  images?: { id: number; name: string }[];
  conditionStatus?: 'green'|'yellow'|'red';
  conditionDay?: string | null;
  daysOut?: number|null;
}
interface Snapshot {
  canEditDaysOut?: boolean;
  supportsCombinedRequests?: boolean;
  requests: CapacityRequest[];
  notes: Record<string, MarketNote>;
  previousDayConditions?: Record<string, {
    conditions: string;
    condition_status: 'green'|'yellow'|'red';
    days_out: number | null;
  }>;
  managers: string[];
  loading: boolean;
  error: string;
  /** A read has succeeded at least once, so empty collections mean "none"
   *  rather than "never loaded". Without it a failed load is indistinguishable
   *  from a quiet day, and the UI renders defaults as though they were facts. */
  loaded: boolean;
}
let snapshot: Snapshot = { requests: [], notes: {}, managers: [], loading: true, error: '', loaded: false };
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(fn => fn());
let poll: ReturnType<typeof setInterval> | undefined;
const subscribe = (fn: () => void) => {
  listeners.add(fn);
  if (!poll) poll = setInterval(() => void refreshCapacity(), 30000);
  return () => { listeners.delete(fn); if (!listeners.size) { clearInterval(poll); poll = undefined; } };
};
let pending: Promise<void> | undefined;
function errorMessage(error: unknown) {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  const detail = response?.data?.detail;
  if (Array.isArray(detail)) return detail.map(item => typeof item?.msg === 'string' ? item.msg : 'Invalid request field').join('; ');
  if (typeof detail === 'string') return detail;
  // Say which kind of failure it was. The old catch-all blamed "the API and
  // migration" for everything, including a plain missing permission.
  if (response?.status === 403) return 'You do not have permission to view capacity requests. Ask an administrator to grant access.';
  if (response?.status === 401) return 'Your session has expired. Reload the page to sign in again.';
  if (response?.status === 404) return 'Capacity requests are unavailable on this server (endpoint not found).';
  if (response && response.status && response.status >= 500) return 'The server failed to load capacity requests. Check the API log, then retry.';
  // No response object at all. Either the server is unreachable, or it threw
  // before the CORS middleware ran and the browser blocked the reply -- the
  // browser does not say which, so the message must not claim to know.
  if (!response) return 'No response from the server. It may be unreachable, or it failed before it could reply — check the API log, then retry.';
  return 'Capacity requests could not be saved or loaded. Check the API and migration, then retry.';
}
let loadedAt = 0;
/** A mount-time read is skipped while the snapshot is younger than this. */
const FRESH_MS = 15000;

export async function refreshCapacity() {
  if (pending) return pending;
  pending = (async () => {
    try {
      const { data } = await api.get('/capacity-requests');
      loadedAt = Date.now();
      // A poll that found nothing new must not replace the snapshot: every
      // consumer re-renders on identity, and effects keyed on `requests` would
      // refetch and blank their own panels on an unchanged queue. Compare
      // against the live snapshot rather than the last response, so a write
      // that replaced it in between is accounted for.
      const next = { ...data, loading: false, error: '', loaded: true };
      if (snapshot.loaded && !snapshot.error && JSON.stringify(next) === JSON.stringify(snapshot)) return;
      snapshot = next;
    } catch (error) { snapshot = { ...snapshot, loading: false, error: errorMessage(error) }; }
    emit();
  })();
  try { await pending; } finally { pending = undefined; }
}

/**
 * Mount-time load. Eight components read this store and they remount whenever
 * the market tab changes, so an unconditional fetch per mount drove the queue
 * far harder than its own 30s poll. Explicit refreshes still always fetch.
 */
export async function ensureCapacity() {
  if (snapshot.loaded && Date.now() - loadedAt < FRESH_MS) return;
  await refreshCapacity();
}

export function useCapacityPreview() {
  useEffect(() => { void ensureCapacity(); }, []);
  return useSyncExternalStore(subscribe, () => snapshot);
}
/** Record a failed write on the shared snapshot and rethrow it for the caller. */
function failWrite(error: unknown): never {
  const message = errorMessage(error);
  snapshot = { ...snapshot, error: message }; emit();
  throw Object.assign(new Error(message), { cause: error });
}
async function mutate(method: 'post' | 'put', path: string, body: unknown) {
  try {
    const { data } = await api[method](`/capacity-requests${path}`, body);
    snapshot = { ...data, loading: false, error: '', loaded: true };
    emit();
  } catch (error) {
    failWrite(error);
  }
}
export async function addRequests(rows: CapacityRequest[], files: File[] = [], combine = false) {
  if (combine && !snapshot.supportsCombinedRequests) throw new Error('Shared requests are not available on this server yet. Choose separate requests or try again after the server is updated.');
  if (files.length > 4 || files.some(f => f.size > 5 * 1024 * 1024) || files.reduce((sum, f) => sum + f.size, 0) * (combine ? 1 : rows.length) > 20 * 1024 * 1024) throw new Error('Use up to four documents, 5 MB each and 20 MB total across this batch.');
  const documents = await Promise.all(files.map(file => new Promise<{ name: string; content: string }>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({ name: file.name, content: String(reader.result).split(',')[1] });
    reader.onerror = () => reject(new Error('Could not read document.'));
    reader.readAsDataURL(file);
  })));
  await mutate('post', '', { combine, requests: rows.map((row, index) => ({ ...row, attachments: [], documents: !combine || index === 0 ? documents : [] })) });
}
export async function decideRequests(rows: CapacityRequest[], action: Exclude<Status, 'PENDING' | 'COMPLETED'> | 'ACCEPT' | 'DECLINE' | 'REPLY', date = '', comment = '') {
  await mutate('post', '/decisions', { decisions: rows.map(r => ({ id: r.id, version: r.version ?? 1, action, date: date || null, comment })) });
}
export async function saveMarketNote(market: string, text: string, version = snapshot.notes[market]?.version ?? 0, images: string[] = [], keepImageIds: number[] = [], daily?: { condition_status: 'green'|'yellow'|'red'; days_out: number|null }) {
  // Market conditions are their own resource, and saving a note leaves the
  // capacity queue untouched -- so the reply carries notes only, and we merge
  // rather than replacing a snapshot the server no longer re-reads.
  try {
    const daysOutChanged = daily !== undefined && daily.days_out !== (snapshot.notes[market]?.daysOut ?? null);
    const { data } = await api.put(`/market-conditions/${encodeURIComponent(market)}`,
      { text, version, images, keep_image_ids: keepImageIds, ...daily });
    snapshot = { ...snapshot, notes: data?.notes ?? snapshot.notes, loading: false, error: '', loaded: true };
    emit();
    if (daysOutChanged) {
      const day = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Chicago' }).format(new Date());
      await api.put('/market-conditions/days-out', { market, day, days_out: daily.days_out });
      await refreshCapacity();
    }
  } catch (error) {
    failWrite(error);
  }
}
export async function linkRequests(id: number, pros: string[]) {
  const row = snapshot.requests.find(r => r.id === id);
  if (!row) throw new Error('Request not found. Refresh the queue.');
  await mutate('post', `/${id}/links`, { pros, version: row.version ?? 1 });
}
const pendingOrderContexts = new Map<string, Promise<OrderContext>>();
export function loadOrderContext(pro: string): Promise<OrderContext> {
  const pending = pendingOrderContexts.get(pro);
  if (pending) return pending;
  // Share only in-flight work. Reopening the form must still load current PRO data.
  const request = api.get(`/capacity-requests/orders/${encodeURIComponent(pro)}`)
    .then(result => result.data as OrderContext)
    .finally(() => pendingOrderContexts.delete(pro));
  pendingOrderContexts.set(pro, request);
  return request;
}

// Order details can use US display dates while search results use ISO dates.
export function capacityDate(value?: string): string {
  if (!value) return '';
  const iso = value.match(/^(\d{4}-\d{2}-\d{2})/);
  if (iso) return iso[1];
  const us = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  return us ? `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}` : '';
}

export async function downloadDocument(id: number, name: string) {
  const result = await api.get(`/capacity-requests/documents/${id}`, { responseType: 'blob' });
  const url = URL.createObjectURL(result.data);
  const anchor = document.createElement('a'); anchor.href = url; anchor.download = name; anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export function useCapacityQueue(view: 'OPEN' | 'APPROVED' | 'DENIED' | 'CANCELLED' | 'COMPLETED', days: number) {
  const [state, setState] = useState({ requests: [] as CapacityRequest[], openRequests: [] as CapacityRequest[], loading: true, error: '' });
  useEffect(() => {
    let active = true;
    const refresh = async () => {
      try {
        const [selected, open] = await Promise.all([
          api.get('/capacity-requests', { params: { view, days } }),
          view === 'OPEN' ? Promise.resolve(null) : api.get('/capacity-requests', { params: { view: 'OPEN' } }),
        ]);
        if (active) setState({ requests: selected.data.requests,
          openRequests: ((open ?? selected).data.requests as CapacityRequest[]).filter(isOpen),
          loading: false, error: '' });
      } catch (error) { if (active) setState(s => ({ ...s, loading: false, error: errorMessage(error) })); }
    };
    setState(s => ({ ...s, requests: [], loading: true, error: '' }));
    void refresh();
    const timer = setInterval(() => void refresh(), 30000);
    listeners.add(refresh);
    return () => { active = false; clearInterval(timer); listeners.delete(refresh); };
  }, [view, days]);
  return state;
}
